import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import EastIcon from "@mui/icons-material/East"
import { Grid } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import { getPosition } from "../../utils/genericHelpers"
import type { MessageItemProps } from "./interfaces"

export function MessageItem({ title, component, comment, componentName }: Readonly<MessageItemProps>): ReactElement {
  const { t } = useTranslation("KonanAssistant")
  const location = useLocation()

  const getDestinationName = (): string => {
    switch (componentName?.toLowerCase()) {
      case "ruleset":
        return t("helpers.componentTypes.rulesets")
      case "taglist":
        return t("helpers.componentTypes.taglists")
      case "scorecard":
      case "scorecardset":
        return t("helpers.componentTypes.scorecards")
      case "script":
        return t("helpers.componentTypes.scripts")
      default:
        return ""
    }
  }

  const handleNavigation = (): void => {
    const destination = getDestinationName()
    if (destination) {
      const targetUrl = location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat(`/${destination}`)
      window.open(targetUrl, "_blank")
    }
  }

  return (
    <Grid item xs={12} gap={1} display="flex">
      <Grid item width="32px" display="flex" justifyContent="center">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "23px",
            height: "23px",
            backgroundColor: "var(--green-background-2)",
            borderRadius: "50%",
          }}
        >
          <CheckIcon
            fontSize="inherit"
            style={{ fill: "var(--green-background-1)", fontSize: "16px", fontWeight: 900 }}
          />
        </div>
      </Grid>

      <Grid container item xs display="flex" alignSelf="center" gap={1}>
        <Grid item xs={12}>
          <Typography variant="h3-bold">{title}</Typography>
          <Typography variantColor={2}>{comment}</Typography>
        </Grid>

        {component && (
          <Grid item xs={12}>
            {component}
          </Grid>
        )}

        <Grid item xs={12} display="flex" alignItems="center" justifyContent="end" gap={1.5} paddingTop={1}>
          <Grid item display="flex" alignItems="center" gap={0.5}>
            <CheckIcon style={{ fontSize: "1.1rem", color: "var(--green-background-1)" }} />
            <Typography variant="label-bold" style={{ color: "var(--green-background-1)" }}>
              {t("helpers.savedSuccessfully")}
            </Typography>
          </Grid>

          {componentName !== "Label" && (
            <Button variant="secondary" size="small" onClick={handleNavigation}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
                {t("helpers.navigateTo")} {getDestinationName()} <EastIcon style={{ fontSize: "0.8rem" }} />
              </div>
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
